import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import logo from '../assets/logo.png';

const NAV_LINKS = [
  { to: '/le-projet', label: 'Le Projet' },
  { to: '/fonctionnalites', label: 'Fonctionnalités' },
  { to: '/famille', label: 'Famille' },
  { to: '/vie-privee', label: 'Vie Privée' },
  { to: '/communaute', label: 'Communauté' },
];

const linkStyle = ({ isActive }) => ({
  color: isActive ? 'var(--mga-gold)' : 'var(--mga-slate-300)',
  fontSize: '0.95rem',
  fontWeight: isActive ? 700 : 500,
  transition: 'color 0.2s ease',
  textDecoration: 'none',
});

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Ferme le menu mobile à chaque changement de page
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <header style={{
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      backgroundColor: 'rgba(5, 11, 20, 0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
      padding: '10px 16px',
      transition: 'all 0.3s ease',
    }}>
      <style>{`
        .navbar-toggle { display: none; }
        .navbar-mobile-panel { display: none; }
        @media (max-width: 900px) {
          .navbar-nav { display: none !important; }
          .navbar-toggle { display: inline-flex !important; }
          .navbar-cta-desktop { display: none !important; }
          .navbar-inner { flex-wrap: nowrap !important; justify-content: space-between !important; gap: 8px !important; }
          .navbar-brand img { height: 32px !important; }
          .navbar-brand span { font-size: 1.1rem !important; }
          .navbar-mobile-panel.is-open {
            display: flex !important;
            flex-direction: column;
            gap: 4px;
            max-width: 1200px;
            margin: 12px auto 0 auto;
            padding-top: 12px;
            border-top: 1px solid rgba(255, 255, 255, 0.08);
          }
          .navbar-mobile-panel a {
            padding: 12px 6px;
            border-radius: 8px;
          }
          .navbar-mobile-panel a:hover {
            background: rgba(255, 255, 255, 0.04);
          }
        }
      `}</style>
      <div className="navbar-inner" style={{
        maxWidth: '1200px',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        {/* Logo and Brand */}
        <Link to="/le-projet" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}>
          <img
            src={logo}
            alt="Logo MonAlerte"
            style={{
              height: '42px',
              width: 'auto',
              filter: 'drop-shadow(0 2px 8px rgba(242,184,7,0.3))'
            }}
          />
          <span style={{
            color: 'var(--mga-white)',
            fontWeight: 800,
            fontSize: '1.3rem',
            letterSpacing: '-0.5px',
            fontFamily: 'var(--font-display)'
          }}>
            Mon<span style={{ color: 'var(--mga-gold)' }}>Alerte</span>
          </span>
        </Link>

        {/* Links & CTA (desktop) */}
        <div className="navbar-links" style={{ display: 'flex', alignItems: 'center', gap: '28px', flexWrap: 'wrap' }}>
          <nav className="navbar-nav" style={{ display: 'flex', gap: '22px', alignItems: 'center' }}>
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} style={linkStyle}>
                {link.label}
              </NavLink>
            ))}
            <NavLink
              to="/simulateur"
              style={({ isActive }) => ({
                color: 'var(--mga-gold)',
                fontSize: '0.95rem',
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: isActive ? 'rgba(242, 184, 7, 0.25)' : 'rgba(242, 184, 7, 0.12)',
                padding: '6px 12px',
                borderRadius: '8px',
                border: '1px solid rgba(242, 184, 7, 0.3)',
                transition: 'all 0.2s ease',
                textDecoration: 'none',
              })}
            >
              🎮 Simulateur Live
            </NavLink>
          </nav>

          <Link
            to="/rejoindre"
            className="button-primary navbar-cta navbar-cta-desktop"
            style={{
              padding: '10px 20px',
              fontSize: '0.9rem',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(242,184,7,0.15)'
            }}
          >
            Rejoindre gratuitement
          </Link>

          {/* Bouton hamburger (mobile) */}
          <button
            type="button"
            className="navbar-toggle"
            onClick={() => setIsOpen((v) => !v)}
            aria-label={isOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={isOpen}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: 'var(--mga-white)',
              cursor: 'pointer',
            }}
          >
            {isOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Panneau mobile déroulant */}
        <nav className={`navbar-mobile-panel${isOpen ? ' is-open' : ''}`} style={{ width: '100%' }}>
          {NAV_LINKS.map((link) => (
            <NavLink key={link.to} to={link.to} style={linkStyle}>
              {link.label}
            </NavLink>
          ))}
          <NavLink to="/simulateur" style={{ color: 'var(--mga-gold)', fontWeight: 700, textDecoration: 'none' }}>
            🎮 Simulateur Live
          </NavLink>
          <Link
            to="/rejoindre"
            className="button-primary"
            style={{ marginTop: '10px', textAlign: 'center', justifyContent: 'center' }}
          >
            Rejoindre gratuitement
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
